import { QueryClient } from '@tanstack/react-query';
import { ServerEventValidationSchema } from '@thedealersconcierge/lib/cache/events';
import { platformVersionKeys } from './keys/platformVersion';
import { transactionKeys } from './keys/transaction';
import { DealershipScope } from './scope';

const assertNever = (event: never): never => {
  throw new Error(`Unhandled server event: ${JSON.stringify(event)}`);
};

/**
 * Apply a backend wire event to the TanStack cache.
 *
 * Most events are thin notifications (type + ids) that we turn into an
 * invalidation — we never read entity data from them, and because keys are
 * hierarchical a coarse event such as `transaction.updated` cascades to every
 * sub-resource of that transaction. `platform.version` is the exception: it
 * carries its value, which we write straight to the cache (no refetch).
 *
 * Invalidation only *refetches* queries that are currently active (have a
 * mounted observer — i.e. on screen): that is React Query's default
 * `refetchType: 'active'`, and we pass no override. So an event for a
 * transaction that is NOT in view never triggers a background fetch — it is a
 * no-op when the query is uncached, or just marks a cached-but-inactive query
 * stale so it refetches the next time it mounts.
 *
 * `scope` is supplied by the caller — the SSE connection already knows the
 * active dealership + viewer — so the event itself never carries the viewer.
 * The `switch` is exhaustive (`assertNever`): adding a variant to the shared
 * schema is a compile error until it is handled here, which keeps the event
 * taxonomy and the key taxonomy from drifting apart.
 */
export const applyServerEvent = (
  queryClient: QueryClient,
  scope: DealershipScope,
  event: ServerEventValidationSchema
): void => {
  switch (event.type) {
    case 'transaction.updated':
      void queryClient.invalidateQueries({
        queryKey: transactionKeys.detail(scope, event.transactionId)
      });
      return;
    case 'platform.version':
      // Not an invalidation: the heartbeat carries the serving backend's version,
      // so we write it straight to the cache (no refetch). The version-mismatch
      // prompt in _app observes this entry; this frame also serves as keep-alive.
      queryClient.setQueryData(
        platformVersionKeys.current(),
        event.platformVersion
      );
      return;
    default:
      return assertNever(event);
  }
};
